/****************************************************************************************
 * File Name    : FilterProcessWorkReport.vue
 * Function     : 필터 공정순회 점검일지 - 일지관리
 * Author       : 
 * Description  : 공정순회 점검일지 보고서 관리
 * Modification Log
 * ======================================================================================
 * Ver  Date        	Author     	Modification
 * ======================================================================================
   1.0  2026.02.17  	        	Create
****************************************************************************************/
<template>
    <div>
        <a-row :gutter="16">
            <a-col class="gutter-row" :span="12">
                <div class="gutter-box">
                    <PageHeader title="필터 공정순회 점검일지 - 일지관리" />
                </div>
            </a-col>
        </a-row>
        <a-divider type='horizontal' />
        
        <a-form class="" layout="vertical">
            <a-card>
                <a-space align="end">
                    <a-input default-value="" v-model="filterCS">
                        <a-select slot="addonAfter" default-value="라인명" v-model="filterField">
                            <a-select-option value="라인명">라인명</a-select-option>
                            <a-select-option value="생산모델">생산모델</a-select-option>
                            <a-select-option value="작성자">작성자</a-select-option>
                        </a-select>
                    </a-input>

                    <span style="display: inline-block; margin-bottom: 5px; margin-left: 10px;">일자</span>
                    <date-picker v-model="filterDateStartCS" valueType="format" style="width:120px;"></date-picker>
                    <span style="display: inline-block; margin-bottom: 5px;">~</span>
                    <date-picker v-model="filterDateEndCS" valueType="format" style="width:120px;"></date-picker>
                    
                    <a-button type="primary" ghost="true" @click="filterCSList">검색</a-button>
                    <a-button @click="resetCSList">초기화</a-button>
                </a-space>
                <p/>
                <div style="position:relative;">
                    <img id="loading-data" class="more-btn" src="../assets/images/Loading_2.gif" v-if="isLoading" style="position:absolute; top:50%; left:50%; margin-top:-30px; margin-left:-30px; width:60px; z-index:10;" />
                    <a-table :columns="columns" size="small" :bordered="false" :data-source="data" :pagination="pagination" :scroll="{ x: 0, y: '80vh' }" @change="handleTableChange">
                        <span slot="tags" slot-scope="tags">
                            <a-tag v-for="tag in tags" :key="tag" class="state-btn" :class="{ green: (tag === '작성완료'), navy: (tag === '승인완료') }">
                                {{ tag.toUpperCase() }}
                            </a-tag>
                        </span>
                        <span slot="name" slot-scope="text">{{ text }}</span>
                        <span slot="checkResult" slot-scope="text">
                            <a-tag :color="text === 'OK' ? 'green' : 'red'">
                                {{ text }}
                            </a-tag>
                        </span>
                        <span slot="action" slot-scope="record">
                            <span style='cursor:pointer' @click="handlerToView(record.key)"><a-icon type="eye" /> View</span>
                            <a-divider type="vertical" />
                            <span style='cursor:pointer' @click="handlerToApprove(record.key)" v-if="record.tags[0] === '작성완료'"><a-icon type="check-circle" /> 승인</span>
                            <span style='cursor:pointer' @click="handlerToCancelApprove(record.key)" v-if="record.tags[0] === '승인완료'"><a-icon type="close-circle" /> 승인취소</span>
                            <a-divider type="vertical" />
                            <span style='cursor:pointer' @click="handlerToDelete(record.key)"><a-icon type="delete" /> Delete</span>
                        </span>
                    </a-table>
                </div>
            </a-card>
        </a-form>

        <!-- 일지 상세보기 Drawer -->
        <a-drawer 
            title="공정순회 점검일지 상세보기" 
            placement="right" 
            :closable="true" 
            :visible="flagForDrawer" 
            @close="handlerForCloseDrawer" 
            width="100%"
        >
            <a-form layout="vertical">
                <div id="print">
                <!-- 기본정보 -->
                <a-card title="기본정보" :bordered="false" style="margin-bottom: 16px;">
                    <a-descriptions bordered :column="2">
                        <a-descriptions-item label="라인명">{{ viewData.basicInfo.lineName }}</a-descriptions-item>
                        <a-descriptions-item label="작성자">{{ viewData.basicInfo.writer }}</a-descriptions-item>
                        <a-descriptions-item label="생산 모델">{{ viewData.basicInfo.productModel }}</a-descriptions-item>
                        <a-descriptions-item label="승인자명">{{ viewData.basicInfo.approverName || '-' }}</a-descriptions-item>
                        <a-descriptions-item label="승인일자">{{ viewData.basicInfo.approvalDate || '-' }}</a-descriptions-item>
                        <a-descriptions-item label="승인자 서명">
                            <div style="display:inline-block; border:1px dashed #d9d9d9; padding:2px 8px; min-width:100px; min-height:36px; text-align:center; vertical-align:middle;">
                                <span v-if="!signatureImage" style="color:#bbb; line-height:30px;">(서명없음)</span>
                                <img v-if="signatureImage" :src="signatureImage" style="max-height:40px; max-width:140px; display:block;" />
                            </div>
                        </a-descriptions-item>
                    </a-descriptions>
                </a-card>

                <!-- 공정별 점검 결과 -->
                <a-card title="공정별 점검 결과" :bordered="false" style="margin-bottom: 16px;">
                    <div style="overflow-x: auto;">
                        <table class="fpr-table">
                            <thead>
                                <tr>
                                    <th style="width:50px;">No</th>
                                    <th style="width:130px;">공정명</th>
                                    <th>Check Point</th>
                                    <th v-for="line in viewData.checkLines" :key="line.id" style="width:100px; min-width:90px;">{{ line.lineName }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <template v-for="(proc, pIdx) in viewData.processes">
                                    <tr v-for="rowNum in (proc.rowCount || 1)" :key="`${pIdx}_${rowNum}`">
                                        <td v-if="rowNum === 1" :rowspan="proc.rowCount || 1" class="fpr-center fpr-no">{{ pIdx + 1 }}</td>
                                        <td v-if="rowNum === 1" :rowspan="proc.rowCount || 1" class="fpr-center">{{ proc.processName }}</td>
                                        <td v-if="rowNum === 1" :rowspan="proc.rowCount || 1" class="fpr-cp">{{ proc.checkPoint }}</td>
                                        <td v-for="line in viewData.checkLines" :key="line.id" class="fpr-result">
                                            <template v-if="proc.results[line.id + '_' + rowNum]">
                                                <a-tag :color="proc.results[line.id + '_' + rowNum] === 'OK' ? 'green' : 'red'">
                                                    {{ proc.results[line.id + '_' + rowNum] }}
                                                </a-tag>
                                            </template>
                                            <span v-else style="color:#ccc;">-</span>
                                        </td>
                                    </tr>
                                </template>
                            </tbody>
                        </table>
                    </div>
                </a-card>

                <a-divider />
                <div id="print-button-container" style="margin-top:20px; text-align:center;">
                    <a-button type="default" icon="plus-circle" size="default" @click="handlerForCloseDrawer">
                        나가기
                    </a-button>
                    <a-button type="default" icon="plus-circle" size="default" @click="print()" style="margin-left:20px;">
                        인쇄
                    </a-button>
                    <a-button v-if="currentViewState !== 'FP_F'" type="primary" icon="check-circle" size="default" @click="handlerForApproveFromDrawer(true)" style="margin-left:20px;">
                        승인완료
                    </a-button>
                    <a-button v-if="currentViewState === 'FP_F'" type="danger" icon="close-circle" size="default" @click="handlerForApproveFromDrawer(false)" style="margin-left:20px;">
                        승인완료 취소
                    </a-button>
                </div>
                </div><!-- /#print -->
            </a-form>
        </a-drawer>
    </div>
</template>

<script>
import DatePicker from 'vue2-datepicker';
import PageHeader from "../components/PageHeader.vue";

const columns = [
    { title: '상태', dataIndex: 'tags', key: 'tags', scopedSlots: { customRender: 'tags' }, width: 100 },
    { title: '라인명', dataIndex: 'lineName', key: 'lineName' },
    { title: '생산모델', dataIndex: 'productModel', key: 'productModel' },
    { title: '작성자', dataIndex: 'writer', key: 'writer' },
    { title: '작성일자', dataIndex: 'date', key: 'date' },
    { title: '최종수정일', dataIndex: 'updated', key: 'updated' },
    { title: '점검결과', dataIndex: 'checkResult', key: 'checkResult', scopedSlots: { customRender: 'checkResult' } },
    { title: '관리', key: 'action', scopedSlots: { customRender: 'action' }, width: 280 },
];


export default {
    name: 'FilterProcessWorkReport',
    components: {
        PageHeader,
        DatePicker,
    },
    data() {
        return {
            columns,
            data: [],
            filterCS: '',
            filterField: '라인명',
            filterDateStartCS: '',
            filterDateEndCS: '',
            isLoading: false,
            pagination: {
                current: 1,
                pageSize: 10,
                total: 0,
                showSizeChanger: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} / ${total}개`
            },
            flagForDrawer: false,
            currentViewKey: null,
            currentViewState: '',
            signatureImage: null,
            viewData: {
                basicInfo: {
                    lineName: '',
                    writer: '',
                    productModel: '',
                    approverName: '',
                    approvalDate: ''
                },
                processes: [],
                checkLines: []
            }
        };
    },
    mounted() {
        this.fetchReportList();
    },
    methods: {
        fetchReportList() {
            this.isLoading = true;
            const page = this.pagination.current - 1;
            const size = this.pagination.pageSize;
            let url = `${this.$store.state.nodeApiUrl}/filter/process/report/list?size=${size}&page=${page}`;
            
            if (this.filterDateStartCS) url += `&start_date=${this.filterDateStartCS}`;
            if (this.filterDateEndCS) url += `&end_date=${this.filterDateEndCS}`;
            
            this.$http.get(url)
                .then((response) => {
                    if (response.status === 200 && response.data.code === 'success') {
                        this.pagination.total = response.data.data.totalElements || 0;
                        this.data = response.data.data.content.map(item => ({
                            key: item.productCsSeq,
                            tags: [item.state === 'FP_D' ? '작성완료' : '승인완료'],
                            lineName: item.line,
                            productModel: item.productModel || '-',
                            writer: item.manager,
                            date: item.updated ? item.updated.split(' ')[0] : '',
                            updated: item.updated,
                            checkResult: item.checkResult || 'OK'
                        }));
                    }
                    this.isLoading = false;
                })
                .catch((error) => {
                    console.error(error);
                    this.$message.error('목록 조회에 실패했습니다.');
                    this.isLoading = false;
                });
        },
        filterCSList() {
            console.log('Filter:', this.filterField, this.filterCS);
            this.fetchReportList();
        },
        resetCSList() {
            this.filterCS = '';
            this.filterDateStartCS = '';
            this.filterDateEndCS = '';
            this.fetchReportList();
        },
        handleTableChange(pagination) {
            this.pagination.current = pagination.current;
            this.pagination.pageSize = pagination.pageSize;
            this.fetchReportList();
        },
        handlerToView(key) {
            const listItem = this.data.find(d => d.key === key);
            this.currentViewKey = key;
            this.currentViewState = listItem ? (listItem.tags[0] === '승인완료' ? 'FP_F' : 'FP_D') : 'FP_D';

            this.$http.get(`${this.$store.state.nodeApiUrl}/filter/process/template/item/${key}`)
                .then((response) => {
                    if (response.status === 200 && response.data.code === 'success') {
                        const data = response.data.data;
                        const isApproved = data.state === 'FP_F' || this.currentViewState === 'FP_F';
                        this.viewData.basicInfo.lineName = data.line || '';
                        this.viewData.basicInfo.writer = data.manager || '';
                        this.viewData.basicInfo.productModel = data.productModel || '';
                        this.viewData.basicInfo.approverName = isApproved
                            ? (data.approver || data.updater || '')
                            : '';
                        this.viewData.basicInfo.approvalDate = isApproved ? (data.updated || '') : '';

                        const checkLines = (data.checkLines || []).map(l => ({ ...l, id: l.lineSeq }));
                        this.viewData.checkLines = checkLines;

                        this.viewData.processes = (data.processes || []).map(p => {
                            const rowCount = p.rowCount || 1;
                            const savedResults = p.results || {};
                            // 이전 형식(results[lineId])을 새 형식(results[lineId_rowNum])으로 마이그레이션
                            const results = {};
                            checkLines.forEach(line => {
                                for (let r = 1; r <= rowCount; r++) {
                                    const newKey = line.id + '_' + r;
                                    if (savedResults[newKey] !== undefined) {
                                        results[newKey] = savedResults[newKey];
                                    } else if (r === 1 && savedResults[line.id] !== undefined) {
                                        results[newKey] = savedResults[line.id];
                                    } else {
                                        results[newKey] = '';
                                    }
                                }
                            });
                            return { ...p, rowCount, results };
                        });

                        this.signatureImage = null;
                        this.flagForDrawer = true;
                        this.fetchSignatureData();
                    }
                })
                .catch((error) => {
                    console.error(error);
                    this.$message.error('데이터 조회에 실패했습니다.');
                });
        },
        handlerToApprove(key) {
            this.$confirm({
                title: '승인 확인',
                content: '해당 일지를 승인하시겠습니까?',
                onOk: () => {
                    this.$http.put(`${this.$store.state.nodeApiUrl}/filter/state/${key}`, {
                        state: 'FP_F',
                        approver: sessionStorage.getItem('user_name') || ''
                    })
                        .then((response) => {
                            if (response.status === 200 && response.data.code === 'success') {
                                this.$message.success('승인되었습니다.');
                                this.fetchReportList();
                            }
                        })
                        .catch((error) => {
                            console.error(error);
                            this.$message.error('승인에 실패했습니다.');
                        });
                }
            });
        },
        handlerToCancelApprove(key) {
            this.$confirm({
                title: '승인취소 확인',
                content: '승인을 취소하시겠습니까?',
                onOk: () => {
                    this.$http.put(`${this.$store.state.nodeApiUrl}/filter/state/${key}`, { state: 'FP_D', approver: '' })
                        .then((response) => {
                            if (response.status === 200 && response.data.code === 'success') {
                                this.$message.success('승인이 취소되었습니다.');
                                this.fetchReportList();
                            }
                        })
                        .catch((error) => {
                            console.error(error);
                            this.$message.error('승인취소에 실패했습니다.');
                        });
                }
            });
        },
        handlerToDelete(key) {
            this.$confirm({
                title: '삭제 확인',
                content: '정말 삭제하시겠습니까?',
                onOk: () => {
                    this.$http.delete(`${this.$store.state.nodeApiUrl}/filter/delete/${key}`)
                        .then((response) => {
                            if (response.status === 200 && response.data.code === 'success') {
                                this.$message.success('삭제되었습니다.');
                                this.fetchReportList();
                            }
                        })
                        .catch((error) => {
                            console.error(error);
                            this.$message.error('삭제에 실패했습니다.');
                        });
                }
            });
        },
        handlerForCloseDrawer() {
            this.flagForDrawer = false;
            this.signatureImage = null;
        },
        print() {
            const prtHtml = document.getElementById('print').innerHTML;

            let stylesHtml = '';
            for (const node of [...document.querySelectorAll('link[rel="stylesheet"], style')]) {
                stylesHtml += node.outerHTML;
            }

            const WinPrint = window.open('', '', 'left=0,top=0,width=1600,height=900,toolbar=0,scrollbars=0,status=0');

            WinPrint.document.write(`<!DOCTYPE html>
            <html>
            <head>
                ${stylesHtml}
            </head>
            <body>
                ${prtHtml}
            </body>
            </html>`);

            WinPrint.document.close();
            WinPrint.focus();
            WinPrint.onload = function() {
                setTimeout(function() {
                    WinPrint.print();
                    WinPrint.close();
                }, 1000);
            };
        },
        handlerForApproveFromDrawer(approve) {
            const newState = approve ? 'FP_F' : 'FP_D';
            const lineName = this.viewData.basicInfo.lineName || '';
            const model = this.viewData.basicInfo.productModel || '';
            const message = approve
                ? `${lineName} ${model} 보고서를 승인하시겠습니까?`
                : `${lineName} ${model} 보고서를 승인취소하시겠습니까?`;

            if (confirm(message)) {
                this.$http.put(`${this.$store.state.nodeApiUrl}/filter/state/${this.currentViewKey}`, {
                    state: newState,
                    approver: approve ? (sessionStorage.getItem('user_name') || '') : ''
                })
                    .then((response) => {
                        if (response.status === 200 && response.data.code === 'success') {
                            this.$message.success(approve ? '승인되었습니다.' : '승인이 취소되었습니다.');
                            this.flagForDrawer = false;
                            this.fetchReportList();
                        }
                    })
                    .catch((error) => {
                        console.error(error);
                        this.$message.error(approve ? '승인에 실패했습니다.' : '승인취소에 실패했습니다.');
                    });
            }
        },
        fetchSignatureData() {
            if (!this.currentViewKey) return;
            const apiUrl = `${this.$store.state.nodeApiUrl}/product/cs/get_signature_data?productCsSeq=${this.currentViewKey}`;
            this.$http.get(apiUrl)
                .then(response => {
                    if (response.status === 200 && response.data.code === 'success' && response.data.data) {
                        this.signatureImage = response.data.data;
                    } else {
                        this.signatureImage = null;
                    }
                })
                .catch(() => {
                    this.signatureImage = null;
                });
        },
    }
};
</script>

<style scoped>
.fpr-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
}
.fpr-table th, .fpr-table td {
    border: 1px solid #d9d9d9;
    padding: 4px 8px;
    vertical-align: middle;
}
.fpr-table th {
    background: #fafafa;
    text-align: center;
    font-weight: 600;
}
.fpr-center { text-align: center; }
.fpr-no { text-align: center; color: #888; }
.fpr-cp { vertical-align: middle; }
.fpr-result { text-align: center; padding: 2px 4px; }
.state-btn {
    font-weight: bold;
    padding: 2px 8px;
}
.state-btn.green {
    background-color: #52c41a;
    color: white;
}
.state-btn.navy {
    background-color: #1890ff;
    color: white;
}
</style>
